/**
 * PipelinePage — one pipeline's home (TRA-863): what it does, and the
 * batches the team has run through it, newest first. New batch opens when
 * the team has an S3 connection to submit from; the API proves access at
 * submit time, so a success here lands on the new batch's page. With no
 * batches yet, the page also shows the POST /v1/batches curl so the first
 * one can come from a script.
 */

import { useState } from 'react'
import { ArrowLeft, Layers, Plus } from 'lucide-react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import { BatchCurlSnippet } from '@/components/pipelines/BatchCurlSnippet'
import { BatchesTable } from '@/components/pipelines/BatchesTable'
import { NewBatchDialog } from '@/components/pipelines/NewBatchDialog'
import { Pager } from '@/components/pipelines/Pager'
import { batchPath } from '@/components/pipelines/paths'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { useTeamConnections } from '@/hooks/useConnections'
import { useBatches, usePipelines } from '@/hooks/usePipelines'

const PAGE_SIZE = 50

export default function PipelinePage(): React.JSX.Element {
  const { name } = useParams<{ name: string }>()
  const { isRestricted } = useTeam()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const offset = Math.max(0, Number(searchParams.get('offset') ?? '0') || 0)
  const pipelines = usePipelines()
  const batches = useBatches({ pipeline: name, offset, limit: PAGE_SIZE })
  const connections = useTeamConnections()
  const [newOpen, setNewOpen] = useState(false)

  if (pipelines.isLoading) {
    return (
      <PageContainer className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </PageContainer>
    )
  }
  if (pipelines.isError) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive">Failed to load pipelines.</p>
      </PageContainer>
    )
  }

  const pipeline = pipelines.data?.pipelines.find((p) => p.name === name)
  // Not granted to the active team (or a team switch): back to the list,
  // not a dead page.
  if (pipeline === undefined) {
    return <Navigate to="/models" replace />
  }

  const connectionItems = connections.data?.connections ?? []
  const canSubmit = !isRestricted && connectionItems.length > 0
  const rows = batches.data?.batches ?? []
  const total = batches.data?.total ?? 0

  function setOffset(next: number): void {
    const params = new URLSearchParams(searchParams)
    if (next <= 0) params.delete('offset')
    else params.set('offset', String(next))
    setSearchParams(params, { replace: true })
  }
  const newBatchButton = (
    <Button
      onClick={() => {
        setNewOpen(true)
      }}
    >
      <Plus className="mr-2 h-4 w-4" />
      New batch
    </Button>
  )

  return (
    <PageContainer className="space-y-6">
      <Link
        to="/models"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Models
      </Link>

      <PageHeader
        title={pipeline.display_name}
        description={pipeline.description}
        badge={
          <div className="flex flex-wrap gap-1">
            {pipeline.stages.map((stage) => (
              <Badge key={stage} variant="outline">
                {stage}
              </Badge>
            ))}
          </div>
        }
        actions={canSubmit ? newBatchButton : undefined}
      />

      {newOpen && (
        <NewBatchDialog
          pipeline={pipeline}
          connections={connectionItems}
          onClose={() => {
            setNewOpen(false)
          }}
          onCreated={(batch) => {
            setNewOpen(false)
            navigate(batchPath(batch))
          }}
        />
      )}

      {batches.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {batches.isError && (
        <p className="text-sm text-destructive">
          Failed to load batches: {getApiErrorDetail(batches.error) ?? 'unknown error'}
        </p>
      )}

      {!batches.isLoading &&
        !batches.isError &&
        (total === 0 ? (
          <EmptyState
            icon={Layers}
            title="No batches yet"
            description="Submit a batch to run this pipeline over every file under an S3 path."
            action={
              <div className="flex flex-col items-center gap-6">
                {canSubmit && newBatchButton}
                {/* The first connection: most teams have one, and a real id
                    beats a placeholder the customer cannot look up. */}
                <BatchCurlSnippet
                  pipeline={pipeline}
                  connectionId={connectionItems[0]?.id ?? null}
                />
              </div>
            }
          />
        ) : (
          <>
            {rows.length > 0 && (
              <div className="rounded-md border border-border">
                <BatchesTable batches={rows} />
              </div>
            )}
            <Pager
              offset={offset}
              count={rows.length}
              total={total}
              pageSize={PAGE_SIZE}
              onOffsetChange={setOffset}
            />
          </>
        ))}
    </PageContainer>
  )
}
